import type { ReactNode } from "react";
import {
  ClockCircleOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import NextReservationNote from "../../../../reservations/components/popover/NextReservationNote";
import JoinedTableNames from "../../../../reservations/components/seat/JoinedTableNames";
import { useReservationFormat } from "../../../../reservations/hooks/useReservationFormat";
import type { TableState } from "../../../../reservations/logic/tableStatus";
import { useTableCardInfo } from "../useTableCardInfo";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { isClosedEmptyBill, type SchemaTableRef } from "./orderingFloorData";

const MetaItem = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5">
    {/* `!` — `.anticon { color: inherit }` на antd е извън Tailwind layer-ите. */}
    <span className="shrink-0 text-[12px] !text-pos-dim">{icon}</span>
    <span className="min-w-0 truncate">{children}</span>
  </span>
);

/** Колоната вдясно — сервитьорът и под него методът на плащане: еднаква
 *  ширина за всяка маса, дългото се реже с „…". */
const SIDE_COLUMN_WIDTH = "w-[104px]";

/** Баджът като на картата в списъка: „Карта — без бон". */
const Badge = ({ children }: { children: ReactNode }) => (
  <span className="shrink-0 rounded-md bg-pos-disc-bg px-[7px] py-0.5 text-[12px] font-semibold leading-[1.3] text-pos-disc-ink">
    {children}
  </span>
);

/**
 * Заглавието на панела в режим „Схема" — СЪЩАТА информация като картата на
 * масата в списъка, но без самата карта, и в ЕДНА подредба за всяка маса
 * (свободна, заета, нефискализирана, временна…): каквото го няма, оставя
 * мястото си празно и нищо друго не го заема.
 * - Ред 1: „МАСА" и името (акцентно при отворена сметка; „Една сметка" за
 *   резервация на няколко маси — всичките ѝ маси: „1 | 2 | 3"); вдясно —
 *   сервитьорът (на свободна — нищо). Процентът на клиента е горе вдясно на
 *   масата в схемата и в лентата на клиента под заглавието.
 * - Ред 2: местата (и на нефискализирана с името на маса от схемата;
 *   временната — броят гости, ако е въведен, иначе „-"), кога е открита (и
 *   кога е закрита), после броят гости и „Карта — без бон"; методът на
 *   плащане (нефискализирана) — точно под сервитьора.
 * Сервитьорът и методът на плащане са в една колона с еднаква ширина (иконките
 * една под друга; дългото име се реже с „…"), местата — също.
 * Отдолу — резервацията на масата; при няколко отворени сметки на една маса
 * (лична номерация) — ред за избор между тях. „Закрива се" е върху самата маса
 * на схемата / плочката в лентата.
 */
export const SchemaTableHeader = ({
  table,
  tableState,
  takenByWaiterName,
  seats,
  bill,
  billTableNames,
  tableBills,
  onSelectBill,
}: {
  /** Масата от схемата; null — временна маса. */
  table: SchemaTableRef | null;
  tableState: TableState | null;
  /** Сервитьорът на чуждата сметка на масата, която списъкът не вижда. */
  takenByWaiterName: string | null;
  /** Местата на масата от схемата с това име; null — няма такава (временна). */
  seats: number | null;
  bill: BillOut | null;
  /** Масите, които сметката държи сама; при повече от една — името е
   *  всичките (коя от тях е кликната, няма значение). */
  billTableNames: string[];
  /** Сметките на масата — изборът се показва при две и повече. */
  tableBills: BillOut[];
  onSelectBill: (billId: number) => void;
}) => {
  const { t, i18n } = useTranslation();
  const format = useReservationFormat();
  const { describe } = useTableCardInfo();
  const { data: paymentMethods = [] } = usePaymentMethods();
  const info = bill ? describe(bill) : null;
  const isClosedEmpty = bill ? isClosedEmptyBill(bill) : false;
  const name = table?.table.name ?? bill?.name ?? "";
  const joinedNames = billTableNames.length > 1 ? billTableNames : null;
  const waiterName = info?.waiterLabel ?? takenByWaiterName;

  // Резервацията, която държи масата сега, или следващата след „сега".
  const reservation =
    tableState?.kind === "reserved" ? tableState.reservation : (tableState?.next ?? null);

  const timeOf = (date: Date) =>
    date.toLocaleTimeString("bg-BG", { hour: "2-digit", minute: "2-digit" });
  // „Днес 14:17" / „Вчера 22:10" / „05.10 18:30".
  const stampOf = (iso: string) => {
    const date = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return `${t("common.today")} ${timeOf(date)}`;
    if (date.toDateString() === yesterday.toDateString()) return `${t("common.yesterday")} ${timeOf(date)}`;
    return `${date.toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit" })} ${timeOf(date)}`;
  };
  // Кога е открита: сметката; маса без видима сметка — откогато е заета
  // (настаняването или отварянето на чуждата сметка).
  const openedIso = bill
    ? (bill.restored_at ?? bill.created_at)
    : tableState?.kind === "occupied"
      ? new Date(tableState.sinceMs).toISOString()
      : null;
  const openedAt = openedIso ? stampOf(openedIso) : null;
  // Закрита: открита – закрита (в същия ден — само часът на закриване).
  const closedAt = (() => {
    if (bill?.status !== "closed" || !bill.closed_at) return null;
    const closed = new Date(bill.closed_at);
    const sameDay = openedIso !== null && new Date(openedIso).toDateString() === closed.toDateString();
    return sameDay ? timeOf(closed) : stampOf(bill.closed_at);
  })();
  const paymentMethod = isClosedEmpty
    ? paymentMethods.find((method) => method.id === bill?.payment_method_id)
    : undefined;
  const paymentMethodName = paymentMethod
    ? i18n.language?.startsWith("bg")
      ? paymentMethod.name_bg
      : paymentMethod.name_en || paymentMethod.name_bg
    : null;

  const nameColor = isClosedEmpty
    ? "text-pos-dim"
    : bill
      ? "text-pos-accent"
      : "text-pos-ink";

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-pos-line2 px-5 pb-3 pt-3">
      {/* „МАСА 5" на един ред, сервитьорът вдясно — в колоната на метода на плащане */}
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
          <span className="shrink-0 font-pos-mono text-[11px] font-bold uppercase leading-[1.2] tracking-[.12em] text-pos-muted">
            {t("ordering.schema.tableKicker")}
          </span>
          <span
            title={joinedNames ? joinedNames.join(" | ") : name}
            className={`min-w-0 truncate text-[26px] font-extrabold leading-[1.2] tracking-[-0.4px] ${nameColor}`}
          >
            {joinedNames ? <JoinedTableNames names={joinedNames} /> : name}
          </span>
        </div>
        {waiterName && (
          <span
            title={waiterName}
            className={`flex ${SIDE_COLUMN_WIDTH} shrink-0 text-[12.5px] font-medium leading-[1.3] text-pos-soft2`}
          >
            <MetaItem icon={<UserOutlined />}>{waiterName}</MetaItem>
          </span>
        )}
      </div>

      {/* Три колони с еднаква ширина за всяка маса: местата (временната — без
          маса в схемата — гостите или „-"), часът, методът на плащане под
          сервитьора; каквото липсва, оставя мястото си празно. */}
      <div className="flex min-h-[17px] items-center gap-x-3 text-[12.5px] font-medium leading-[1.3] text-pos-soft2">
        <span className="flex w-[72px] shrink-0">
          <MetaItem icon={<UserOutlined />}>
            {seats !== null
              ? format.seats(seats)
              : bill?.guests != null
                ? format.guests(bill.guests)
                : "-"}
          </MetaItem>
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-x-3 overflow-hidden">
          {openedAt && (
            <MetaItem icon={<ClockCircleOutlined />}>
              {closedAt ? `${openedAt} – ${closedAt}` : openedAt}
            </MetaItem>
          )}
          {seats !== null && bill?.guests != null && (
            <MetaItem icon={<TeamOutlined />}>{format.guests(bill.guests)}</MetaItem>
          )}
          {bill?.card_payment_pending && <Badge>{t("ordering.cardNoReceiptTag")}</Badge>}
        </span>
        <span title={paymentMethodName ?? undefined} className={`flex ${SIDE_COLUMN_WIDTH} shrink-0`}>
          {paymentMethodName && <MetaItem icon={<WalletOutlined />}>{paymentMethodName}</MetaItem>}
        </span>
      </div>

      {reservation && <NextReservationNote reservation={reservation} />}

      {/* Няколко сметки на една маса — изборът между тях */}
      {tableBills.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {tableBills.map((tableBill) => {
            const selected = tableBill.id === bill?.id;
            return (
              <button
                key={tableBill.id}
                type="button"
                onClick={() => onSelectBill(tableBill.id)}
                className={`inline-flex h-8 cursor-pointer items-center gap-2 rounded-[9px] border px-3 text-[13px] font-semibold transition-colors ${
                  selected
                    ? "border-pos-accent bg-pos-marked text-pos-accent-ink"
                    : "border-pos-ctl bg-pos-card text-pos-ink2 hover:bg-pos-panel"
                }`}
              >
                {describe(tableBill).waiterLabel ?? tableBill.name}
                <span className="font-pos-mono text-[12px]">
                  {parseFloat(tableBill.total_amount).toFixed(2)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
